import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AgentEmptyState from '@/components/admin/AgentEmptyState';
import StatCard from '@/components/admin/dashboard/StatCard';
import { formatToolExecution } from '@/lib/tool-name-mapping';
import {
  Bot,
  ListTodo,
  CheckCircle,
  Clock,
  RefreshCw,
  PauseCircle,
  PlayCircle,
  RotateCcw,
  Package,
  Truck,
  ArrowRightLeft,
  AlertTriangle,
  XCircle,
  Eye,
  ExternalLink,
  Search,
  DollarSign,
  CheckCircle2,
} from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';

interface AgentStats {
  active_tasks: number;
  orders_processing: number;
  success_rate_24h: number;
  avg_fulfillment_hours: number;
  pipeline_orders_24h: number;
  pipeline_success_rate: number;
  unread_notifications: number;
  prospects_total: number;
}

interface ActivityItem {
  id: string;
  tool_name: string;
  status: string;
  duration_ms: number | null;
  created_at: string;
  task_type?: string;
}

interface EscalatedTask {
  id: string;
  task_type: string;
  error: string | null;
  trigger_ref: string | null;
  created_at: string;
}

interface FailedOrder {
  id: string;
  order_number: string | null;
  forward_error: string | null;
  created_at: string;
}

interface NeedsAttention {
  escalated_tasks: EscalatedTask[];
  failed_orders: FailedOrder[];
}

interface DashboardResponse {
  stats: AgentStats;
  recent_activity: ActivityItem[];
  needs_attention: NeedsAttention;
}

const statusBadge: Record<string, { variant: 'green' | 'red' | 'blue' | 'yellow' | 'gray'; label: string }> = {
  success: { variant: 'green', label: 'Success' },
  completed: { variant: 'green', label: 'Completed' },
  failed: { variant: 'red', label: 'Failed' },
  running: { variant: 'blue', label: 'Running' },
  pending: { variant: 'yellow', label: 'Pending' },
};

export default function AgentDashboard() {
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [needsAttention, setNeedsAttention] = useState<NeedsAttention | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [lastActionTaskId, setLastActionTaskId] = useState<string | null>(null);
  const [agentPaused, setAgentPaused] = useState(false);
  const addToast = useUIStore((s) => s.addToast);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const res = await api.get<DashboardResponse>('/admin/agent/dashboard');
      setStats(res.stats);
      setActivity(res.recent_activity);
      setNeedsAttention(res.needs_attention ?? null);
    } catch (err) {
      if (!silent) {
        addToast(err instanceof Error ? err.message : 'Failed to load agent dashboard', 'error');
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 30_000);
    return () => clearInterval(interval);
  }, []);

  const handleAction = async (action: string, label: string) => {
    setActionLoading(action);
    setLastActionTaskId(null);
    try {
      const res = await api.post<{ task_id?: string; paused?: boolean }>(`/admin/agent/actions/${action}`);
      if (action === 'pause' || action === 'resume') {
        setAgentPaused(res.paused ?? action === 'pause');
        addToast(res.paused ? 'Agent paused' : 'Agent resumed', 'success');
      } else {
        addToast(`${label} triggered successfully`, 'success');
        if (res.task_id) {
          setLastActionTaskId(res.task_id);
        }
      }
    } catch (err) {
      addToast(err instanceof Error ? err.message : `Failed to trigger ${label}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryTask = async (taskId: string) => {
    try {
      await api.post(`/admin/agent/tasks/${taskId}/retry`);
      addToast('Task queued for retry', 'success');
      fetchData(true);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to retry task', 'error');
    }
  };

  const hasAttentionItems =
    needsAttention &&
    (needsAttention.escalated_tasks.length > 0 || needsAttention.failed_orders.length > 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">AI Dropshipping Agent</h1>
        <p className="text-gray-600 mt-1">Monitor and control the autonomous fulfillment agent.</p>
      </div>

      {/* Guided empty state when everything is zero */}
      {!isLoading && stats && !hasAttentionItems &&
        stats.active_tasks === 0 && stats.orders_processing === 0 && activity.length === 0 && (
          <AgentEmptyState variant="dashboard" />
        )}

      {/* Health badge — system is set up and clean */}
      {!isLoading && stats && !hasAttentionItems && activity.length > 0 && (
        <div
          data-testid="health-banner"
          className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 text-sm text-green-800"
        >
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <span>All clear — no escalated tasks or failed orders.</span>
        </div>
      )}

      {/* Needs Attention Panel */}
      {hasAttentionItems && (
        <div className="space-y-4" data-testid="needs-attention">
          {needsAttention!.escalated_tasks.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-900">
                  Escalated Tasks ({needsAttention!.escalated_tasks.length})
                </h3>
              </div>
              <div className="space-y-2">
                {needsAttention!.escalated_tasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-red-100"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-mono text-gray-800">{task.task_type}</span>
                      {task.error && (
                        <p className="text-xs text-red-600 truncate mt-0.5">{task.error}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      <button
                        onClick={() => handleRetryTask(task.id)}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Retry
                      </button>
                      <Link
                        to={`/admin/agent/tasks/${task.id}`}
                        className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 font-medium"
                      >
                        <Eye className="w-3 h-3" />
                        View
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {needsAttention!.failed_orders.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-5 h-5 text-orange-600" />
                <h3 className="font-semibold text-orange-900">
                  Failed Orders ({needsAttention!.failed_orders.length})
                </h3>
              </div>
              <div className="space-y-2">
                {needsAttention!.failed_orders.map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-orange-100"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-800">
                        {order.order_number ? `Order #${order.order_number}` : 'Supplier Order'}
                      </span>
                      {order.forward_error && (
                        <p className="text-xs text-orange-600 truncate mt-0.5">{order.forward_error}</p>
                      )}
                    </div>
                    <Link
                      to="/admin/supplier-orders"
                      className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 font-medium ml-3 shrink-0"
                    >
                      <Eye className="w-3 h-3" />
                      View
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          elevated
          title="Active Tasks"
          value={isLoading ? '—' : (stats?.active_tasks ?? 0).toString()}
          icon={<ListTodo className="w-6 h-6" />}
          tone="blue"
          to="/admin/agent/tasks?status=executing"
        />
        <StatCard
          elevated
          title="Orders Processing"
          value={isLoading ? '—' : (stats?.orders_processing ?? 0).toString()}
          icon={<Package className="w-6 h-6" />}
          tone="purple"
          to="/admin/agent/tasks?task_type=order_fulfillment"
        />
        <StatCard
          elevated
          title="AI Success Rate (24h)"
          value={isLoading ? '—' : `${Number(stats?.success_rate_24h ?? 0).toFixed(1)}%`}
          icon={<CheckCircle className="w-6 h-6" />}
          tone="green"
        />
        <StatCard
          elevated
          title="Pipeline Orders (24h)"
          value={isLoading ? '—' : (stats?.pipeline_orders_24h ?? 0).toString()}
          icon={<ArrowRightLeft className="w-6 h-6" />}
          tone="indigo"
          to="/admin/supplier-orders"
        />
        <StatCard
          elevated
          title="Pipeline Success Rate"
          value={isLoading ? '—' : `${Number(stats?.pipeline_success_rate ?? 0).toFixed(1)}%`}
          icon={<Truck className="w-6 h-6" />}
          tone="teal"
        />
        <StatCard
          elevated
          title="Avg Fulfillment Time"
          value={isLoading ? '—' : `${Number(stats?.avg_fulfillment_hours ?? 0).toFixed(1)}h`}
          icon={<Clock className="w-6 h-6" />}
          tone="orange"
        />
        <StatCard
          elevated
          title="Supplier Prospects"
          value={isLoading ? '—' : (stats?.prospects_total ?? 0).toString()}
          icon={<Search className="w-6 h-6" />}
          tone="amber"
          to="/admin/agent/prospects"
        />
      </div>

      {/* Recent Activity */}
      <Card padding="none">
        <div className="flex items-center justify-between px-5 pt-5 mb-4">
          <h3 className="text-lg font-semibold text-stone-900">Recent Activity</h3>
          <Link
            to="/admin/agent/tasks"
            className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
          >
            View all activity
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : activity.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="activity-empty">
            <Bot className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="mb-4">No recent activity. Kick the agent off:</p>
            <div className="flex flex-wrap gap-2 justify-center">
              <button
                onClick={() => handleAction('supplier-discovery', 'Supplier Discovery')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-sm font-medium hover:bg-blue-100"
              >
                <Search className="w-3.5 h-3.5" />
                Find Suppliers
              </button>
              <button
                onClick={() => handleAction('sync-catalog', 'Sync Catalog')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-sm font-medium hover:bg-blue-100"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Sync Catalog
              </button>
              <button
                onClick={() => handleAction('inventory-check', 'Inventory Check')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-sm font-medium hover:bg-blue-100"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Run Inventory Check
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tool
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activity.slice(0, 20).map((item) => {
                  const badge = statusBadge[item.status] ?? { variant: 'gray' as const, label: item.status };
                  const formatted = formatToolExecution(item.tool_name);
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-800">{formatted.label}</span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                        {item.task_type ?? '—'}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                        {item.duration_ms != null ? `${item.duration_ms}ms` : '—'}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                        {new Date(item.created_at).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Quick Actions */}
      <Card title="Quick Actions" padding="md">
        <div className="flex flex-wrap gap-3">
          <ActionButton
            label={agentPaused ? 'Resume Agent' : 'Pause Agent'}
            icon={agentPaused ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
            loading={actionLoading === 'pause' || actionLoading === 'resume'}
            onClick={() => handleAction(agentPaused ? 'resume' : 'pause', agentPaused ? 'Resume Agent' : 'Pause Agent')}
            variant={agentPaused ? 'green' : 'yellow'}
            tooltip={agentPaused ? 'Resume the agent — new tasks will be picked up' : 'Pause the agent — running tasks finish, no new ones start'}
          />
          <ActionButton
            label="Run Inventory Check"
            icon={<RefreshCw className="w-4 h-4" />}
            loading={actionLoading === 'inventory-check'}
            onClick={() => handleAction('inventory-check', 'Inventory Check')}
            variant="blue"
            tooltip="Creates a scheduled_check task — track it in Tasks"
          />
          <ActionButton
            label="Sync Catalog"
            icon={<RotateCcw className="w-4 h-4" />}
            loading={actionLoading === 'sync-catalog'}
            onClick={() => handleAction('sync-catalog', 'Sync Catalog')}
            variant="blue"
            tooltip="Creates a catalog_sync task — track it in Tasks"
          />
          <ActionButton
            label="Pricing Review"
            icon={<DollarSign className="w-4 h-4" />}
            loading={actionLoading === 'pricing-review'}
            onClick={() => handleAction('pricing-review', 'Pricing Review')}
            variant="blue"
            tooltip="Creates a pricing_review task — track it in Tasks"
          />
          <ActionButton
            label="Find Suppliers"
            icon={<Search className="w-4 h-4" />}
            loading={actionLoading === 'supplier-discovery'}
            onClick={() => handleAction('supplier-discovery', 'Supplier Discovery')}
            variant="blue"
            tooltip="Creates a supplier_discovery task — track it in Tasks"
          />
          {lastActionTaskId && (
            <Link
              to={`/admin/agent/tasks/${lastActionTaskId}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-green-50 text-green-800 border border-green-200 hover:bg-green-100 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              View Task
            </Link>
          )}
        </div>
      </Card>
    </div>
  );
}

interface ActionButtonProps {
  label: string;
  icon: React.ReactNode;
  loading: boolean;
  onClick: () => void;
  variant: 'yellow' | 'blue' | 'green';
  tooltip?: string;
}

function ActionButton({ label, icon, loading, onClick, variant, tooltip }: ActionButtonProps) {
  const base = 'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50';
  const variants = {
    yellow: 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100',
    blue: 'bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100',
    green: 'bg-green-50 text-green-800 border border-green-200 hover:bg-green-100',
  };
  return (
    <button
      className={cn(base, variants[variant])}
      onClick={onClick}
      disabled={loading}
      title={tooltip}
    >
      {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : icon}
      {label}
    </button>
  );
}
